class NavigationBlock {
  constructor(element) {
    this.navigation = element;

    this.menuTitle = this.navigation.querySelector('.navigation-block__title');
    this.navTrigger = this.navigation.querySelector(
      '.navigation-block__trigger'
    );
    this.navContainer = this.navigation.querySelector('.navigation-block__nav');
    this.menuItems = this.navigation.querySelectorAll(
      'li.menu-item-has-children, li.has-mega-menu'
    );
    this.subMenuToggles = this.navigation.querySelectorAll('.submenu-toggle');

    this.isMobileView = window.innerWidth < 992;
    this.timers = new Map();

    this.config = {
      mobileBreakpoint: 992,
      hoverDelay: 150,
      hoverLeaveDelay: 250,
    };
  }

  init() {
    if (!this.navigation) return;

    this.setupEventListeners();
    this.updateViewMode();
    this.setupInitialAriaStates();
  }

  setupEventListeners() {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.handleResize.bind(this), 150);
    });

    if (this.navTrigger) {
      this.navTrigger.addEventListener('click', this.toggleMainMenu.bind(this));
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllSubmenus();
      }
    });

    document.addEventListener('click', (e) => {
      if (!this.navigation.contains(e.target)) {
        this.closeAllSubmenus();
      }
    });

    this.navigation.addEventListener('click', (e) => {
      const button = e.target.closest('.mega-menu-back-button');
      if (button) {
        e.preventDefault();
        e.stopPropagation();

        const menuItem = button.closest('li.menu-item--open');
        if (menuItem) {
          this.closeSubmenu(menuItem);
        }
        return;
      }

      const closeBtn = e.target.closest('.mega-menu-header__close');
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        window.location.reload();
      }
    });

    this.setupMenuItemHandlers();
  }

  setupMenuItemHandlers() {
    // Znajdź wszystkie menu items włączając zagnieżdżone
    const allMenuItems = this.navigation.querySelectorAll(
      'li.menu-item-has-children, li.has-mega-menu'
    );

    allMenuItems.forEach((item) => {
      const link = item.querySelector(':scope > a');
      const subMenu = item.querySelector(
        ':scope > .sub-menu, :scope > .mega-menu-container'
      );

      if (!subMenu || !link) return;

      // Usuń stare event listenery przez klonowanie
      const newItem = item.cloneNode(true);
      item.parentNode.replaceChild(newItem, item);

      this.attachHandlersToItem(newItem);
    });
  }

  attachHandlersToItem(item) {
    const link = item.querySelector(':scope > a');
    const subMenu = item.querySelector(
      ':scope > .sub-menu, :scope > .mega-menu-container'
    );
    const toggle = item.querySelector(':scope > .submenu-toggle');

    if (!link || !subMenu) return;

    // Toggle button - zawsze otwiera/zamyka submenu
    if (toggle) {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleSubmenu(item);
      });
    }

    if (!this.isMobileView) {
      // === DESKTOP MODE ===

      // Hover na menu item - otwiera submenu
      item.addEventListener('mouseenter', () => {
        this.clearHoverTimer(item);
        const timerId = setTimeout(() => {
          this.openSubmenu(item);
        }, this.config.hoverDelay);
        this.timers.set(item, timerId);
      });

      // Leave z menu item
      item.addEventListener('mouseleave', (e) => {
        this.clearHoverTimer(item);

        const relatedTarget = e.relatedTarget;
        if (
          relatedTarget &&
          (subMenu.contains(relatedTarget) || subMenu === relatedTarget)
        ) {
          return;
        }

        const timerId = setTimeout(() => {
          this.closeSubmenu(item);
        }, this.config.hoverLeaveDelay);
        this.timers.set(item, timerId);
      });

      // Hover na submenu
      if (subMenu) {
        subMenu.addEventListener('mouseenter', () => {
          this.clearHoverTimer(item);
        });

        subMenu.addEventListener('mouseleave', (e) => {
          const relatedTarget = e.relatedTarget;
          if (relatedTarget && item.contains(relatedTarget)) {
            return;
          }

          const timerId = setTimeout(() => {
            this.closeSubmenu(item);
          }, this.config.hoverLeaveDelay);
          this.timers.set(item, timerId);
        });

        // Rekurencyjnie dodaj handlery dla zagnieżdżonych submenu
        const nestedMenuItems = subMenu.querySelectorAll(
          ':scope > li.menu-item-has-children'
        );
        nestedMenuItems.forEach((nestedItem) => {
          this.attachHandlersToItem(nestedItem);
        });
      }

      // Focus handling - otwiera submenu
      link.addEventListener('focus', (e) => {
        this.openSubmenu(item);
      });

      // Focusout - zamyka submenu gdy focus opuszcza całą grupę
      item.addEventListener('focusout', (e) => {
        // Sprawdź czy nowy focus nie jest wewnątrz tego menu item
        const newFocus = e.relatedTarget;
        if (!newFocus || !item.contains(newFocus)) {
          this.closeSubmenu(item);
        }
      });
    } else {
      // === MOBILE MODE ===

      // Link bez toggle otwiera submenu
      if (!toggle) {
        link.addEventListener('click', (e) => {
          if (
            item.classList.contains('menu-item-has-children') ||
            item.classList.contains('has-mega-menu')
          ) {
            e.preventDefault();
            this.toggleSubmenu(item);
          }
        });
      }

      // Rekurencyjnie dodaj handlery dla zagnieżdżonych submenu
      if (subMenu) {
        const nestedMenuItems = subMenu.querySelectorAll(
          ':scope > li.menu-item-has-children'
        );
        nestedMenuItems.forEach((nestedItem) => {
          this.attachHandlersToItem(nestedItem);
        });
      }
    }
  }

  setupInitialAriaStates() {
    const allSubMenus = this.navigation.querySelectorAll(
      '.sub-menu, .mega-menu-container'
    );
    allSubMenus.forEach((menu) => {
      menu.setAttribute('inert', '');
      menu.setAttribute('hidden', '');
    });

    const allMenuItems = this.navigation.querySelectorAll(
      'li.menu-item-has-children, li.has-mega-menu'
    );
    allMenuItems.forEach((item) => {
      const link = item.querySelector(':scope > a');
      const toggle = item.querySelector(':scope > .submenu-toggle');
      link?.setAttribute('aria-expanded', 'false');
      toggle?.setAttribute('aria-expanded', 'false');
    });
  }

  handleResize() {
    const newIsMobileView = window.innerWidth < this.config.mobileBreakpoint;
    if (this.isMobileView !== newIsMobileView) {
      this.isMobileView = newIsMobileView;
      this.closeAllSubmenus();
      this.updateViewMode();
      this.setupMenuItemHandlers();
    }
  }

  updateViewMode() {
    if (this.isMobileView) {
      this.navigation.classList.add('is-mobile-view');
      this.navigation.classList.remove('is-desktop-view');
    } else {
      this.navigation.classList.remove('is-mobile-view');
      this.navigation.classList.add('is-desktop-view');
    }
  }

  toggleMainMenu() {
    if (!this.navContainer) return;
    const isOpen = this.navContainer.classList.contains('is-open');
    this.navContainer.classList.toggle('is-open');
    this.navTrigger.setAttribute('aria-expanded', !isOpen);
    if (isOpen) {
      this.closeAllSubmenus();
    }
  }

  toggleSubmenu(menuItem) {
    const isOpen = menuItem.classList.contains('menu-item--open');
    if (isOpen) {
      this.closeSubmenu(menuItem);
    } else {
      this.openSubmenu(menuItem);
    }
  }

  openSubmenu(menuItem) {
    // Zamknij rodzeństwo na tym samym poziomie
    const parentUl = menuItem.parentElement;
    if (parentUl) {
      Array.from(parentUl.children).forEach((sibling) => {
        if (
          sibling !== menuItem &&
          sibling.classList.contains('menu-item--open')
        ) {
          this.closeSubmenu(sibling);
        }
      });
    }

    const link = menuItem.querySelector(':scope > a');
    const toggle = menuItem.querySelector(':scope > .submenu-toggle');
    const subMenu = menuItem.querySelector(
      ':scope > .sub-menu, :scope > .mega-menu-container'
    );

    menuItem.classList.add('menu-item--open');
    link?.setAttribute('aria-expanded', 'true');
    toggle?.setAttribute('aria-expanded', 'true');

    if (subMenu) {
      subMenu.removeAttribute('hidden');
      subMenu.removeAttribute('inert');
    }
  }

  closeSubmenu(menuItem) {
    if (!menuItem || !menuItem.classList.contains('menu-item--open')) return;

    const link = menuItem.querySelector(':scope > a');
    const toggle = menuItem.querySelector(':scope > .submenu-toggle');
    const subMenu = menuItem.querySelector(
      ':scope > .sub-menu, :scope > .mega-menu-container'
    );

    // Zamknij wszystkie zagnieżdżone submenu
    const nestedOpenItems = menuItem.querySelectorAll('.menu-item--open');
    nestedOpenItems.forEach((nestedItem) => {
      this.closeSubmenu(nestedItem);
    });

    menuItem.classList.remove('menu-item--open');
    link?.setAttribute('aria-expanded', 'false');
    toggle?.setAttribute('aria-expanded', 'false');

    if (subMenu) {
      subMenu.setAttribute('hidden', '');
      subMenu.setAttribute('inert', '');
    }
  }

  closeAllSubmenus() {
    this.navigation.querySelectorAll('.menu-item--open').forEach((item) => {
      this.closeSubmenu(item);
    });
  }

  clearHoverTimer(item) {
    if (this.timers.has(item)) {
      clearTimeout(this.timers.get(item));
      this.timers.delete(item);
    }
  }
}

// Inicjalizacja
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.navigation-block').forEach((element) => {
    const instance = new NavigationBlock(element);
    instance.init();
    element.navigationBlockInstance = instance;
  });
});
